import pjson from '~/package.json';
import useKeyConversion from '~/composables/useKeyConversion';
import type { PublicationContext } from '~/types';

export default function useCachedFetch (
  storageName: string,
  defaultTTL = 3600
) {
  let db = null;

  const openDB = (storage: string) => {
    return new Promise((resolve, reject) => {
      const rtConfPub = useRuntimeConfig()?.public as unknown as PublicationContext;
      const name = `${storage}-${rtConfPub.code || 'cpp'}-${rtConfPub.appEnv || 'def'}`;
      const version = rtConfPub.dbVersion;
      const request = window.indexedDB.open(name, version);

      request.onupgradeneeded = (event) => {
        const db = event.target.result;
        if (db.objectStoreNames.contains('cache')) {
          db.deleteObjectStore('cache');
        }
        const objectStore = db.createObjectStore('cache', { keyPath: 'key' });
        objectStore.createIndex('timestamp', 'timestamp', { unique: false });
      };

      request.onsuccess = (event) => {
        const db = event.target.result;
        resolve(db);
      };

      request.onerror = (event) => {
        reject(event.target.error);
      };
    });
  };

  const getCacheWithCursor = async (callback: (a: any) => void) => {
    if (import.meta.client) {
      db = db || (await openDB(storageName));

      return new Promise((resolve, reject) => {
        const tx = db.transaction('cache', 'readonly');
        const store = tx.objectStore('cache');

        const cursorRequest = store.openCursor();

        const results = [];

        cursorRequest.onsuccess = (event) => {
          const cursor = event.target.result;
          if (cursor) {
            if (callback) {
              callback(cursor.value.value);
            } else {
              results.push(cursor.value.value);
            }
            cursor.continue();
          } else {
            resolve(results);
          }
        };

        cursorRequest.onerror = (event) => {
          reject(event.target.error);
        };
      });
    }

    return false;
  };

  const setCache = async (key: string, data: any, ttl?: number) => {
    ttl = (ttl === 0 ? 0 : (ttl || defaultTTL));
    try {
      if (ttl > 0 && import.meta.client && data) {
        db = db || await openDB(storageName);
        const value = { data, timestamp: Date.now() + ttl * 1000 };
        return new Promise((resolve, reject) => {
          let result;
          const tx = db.transaction('cache', 'readwrite');
          tx.oncomplete = _ => resolve(result);
          tx.onerror = event => reject(event.target.error);
          const store = tx.objectStore('cache');
          const request = store.put({ key, value, timestamp: value.timestamp });
          // eslint-disable-next-line no-return-assign
          request.onsuccess = _ => result = request.result;
        });
      }
    } catch (e) {
      throw createError(`Cannot set cache: , ${key}, ${ttl}, ${data}`);
    }
  };

  const getCache = async (key: string, ttl?: number) => {
    ttl = (ttl === 0 ? 0 : (ttl || defaultTTL));
    if (ttl > 0 && import.meta.client) {
      db = db || await openDB(storageName);
      return new Promise((resolve, reject) => {
        let result;
        const tx = db.transaction('cache', 'readwrite');
        tx.oncomplete = _ => resolve(result);
        tx.onerror = event => reject(event.target.error);
        const store = tx.objectStore('cache');

        const request = store.get(key);
        // eslint-disable-next-line no-return-assign
        request.onsuccess = _ => result = request.result?.value || null;
      });
    }
    return false;
  };

  const cleanCache = async (key: string) => {
    try {
      if (import.meta.client) {
        db = db || await openDB(storageName);
        return new Promise((resolve, reject) => {
          let result;
          const tx = db.transaction('cache', 'readwrite');
          tx.oncomplete = _ => resolve(result);
          tx.onerror = event => reject(event.target.error);
          const store = tx.objectStore('cache');
          const request = store.delete(key);
          // eslint-disable-next-line no-return-assign
          request.onsuccess = _ => result = request.result;
        });
      }
    } catch (e) {
      throw createError(`Cannot clear cache for key , ${key}`);
    }
  };

  const getData = async (key: string, fallback?: any, ttl?: number) => {
    let response = null;
    let loading = true;
    let cachedData = null;
    ttl = (ttl === 0 ? 0 : (ttl || defaultTTL));

    try {
      cachedData = await getCache(key, ttl);
    } catch (error) {
      throw createError(`Cannot get cache data: ${error.message}`);
    }
    if (cachedData) {
      const { data, timestamp } = cachedData;
      const hasValidCache = timestamp - Date.now() > 0;

      if (hasValidCache) {
        response = data;
      } else {
        cleanCache(key);
      }
    }

    if (response === null && fallback) {
      let data = null;
      try {
        data = await fallback();
      } catch (e) {
        throw createError(`Failed to fetch data for: ${key}, ttl: ${ttl} error - ${(e as Error).message}`);
      }
      if (data) {
        response = useKeyConversion('camel').convertKeys(data);
        setCache(key, response, ttl);
      }
    }
    loading = false;

    return { response, loading };
  };

  return {
    getData,
    cleanCache,
    getCache,
    setCache,
    getCacheWithCursor
  };
}
